import { agentHubPaths } from '../../../util/constants';
import {
  getBigCommerceExternalPlatformIdMismatchError,
  normalizeBigCommerceExternalId,
} from '../../../util/bigcommerceExternalId';
import {
  getShopifyExternalPlatformIdMismatchError,
  normalizeShopifyExternalId,
} from '../../../util/shopifyExternalId';
import { normalizeSquarespaceExternalId } from '../../../util/squarespaceExternalId';
import { getSquarespaceSiteError, type SquarespaceStoreLink } from './squarespaceStore';

export const SHOPIFY_PLATFORM_ID = 'shopify';
export const BIGCOMMERCE_PLATFORM_ID = 'bigcommerce';
export const SQUARESPACE_PLATFORM_ID = 'squarespace';

/** The merchant's current store link. Without it, only the format is validated. */
export type YourStoreValidationContext = {
  merchantExternalPlatformId?: string | null;
  squarespaceLink?: SquarespaceStoreLink;
};

/** The "Your store" field, bound to the draft's shopDomain. */
type StoreField = {
  label: string;
  placeholder: string;
  helperText: string;
  testId: string;
  /** Replaces helperText once the address is locked. */
  lockedHelperText?: string;
};

/** Publish success copy and where Continue goes. */
type PublishSuccess = {
  body: string;
  nextSteps: readonly string[];
  continueLabel: string;
  continuePath: string;
};

export type OnboardingPlatform = {
  storeField?: StoreField;
  getStoreError: (shopDomain: string, context: YourStoreValidationContext) => string | undefined;
  isStoreLocked: (context: YourStoreValidationContext) => boolean;
  /** The store address for the publish summary, if one was entered. */
  formatStoreAddress: (shopDomain: string) => string | undefined;
  publishSuccess: PublishSuccess;
};

const getBigCommerceStoreError = (
  shopDomain: string,
  { merchantExternalPlatformId }: YourStoreValidationContext,
): string | undefined => {
  if (normalizeBigCommerceExternalId(shopDomain)) {
    return getBigCommerceExternalPlatformIdMismatchError(shopDomain, merchantExternalPlatformId);
  }
  return shopDomain.trim() ? 'Enter a valid BigCommerce store hash' : 'Store hash is required';
};

const getShopifyStoreError = (
  shopDomain: string,
  { merchantExternalPlatformId }: YourStoreValidationContext,
): string | undefined => {
  if (normalizeShopifyExternalId(shopDomain)) {
    return getShopifyExternalPlatformIdMismatchError(shopDomain, merchantExternalPlatformId);
  }
  return shopDomain.trim()
    ? 'Enter a valid *.myshopify.com shop domain'
    : 'Shop domain is required';
};

const SKIPIFY_ASSISTED_PUBLISH_SUCCESS: PublishSuccess = {
  body: 'A Skipify team member will help with store onboarding and contact you to complete final steps. Once that work is complete, your agent can go live on your storefront.',
  nextSteps: [
    'Finish store onboarding with your Skipify contact',
    'Complete category catalog sync',
    'Enable your agent on your storefront',
  ],
  continueLabel: 'Go to Agents',
  continuePath: agentHubPaths.AGENTS,
};

const noStoreField: Pick<
  OnboardingPlatform,
  'getStoreError' | 'isStoreLocked' | 'formatStoreAddress'
> = {
  getStoreError: () => undefined,
  isStoreLocked: () => false,
  formatStoreAddress: () => undefined,
};

const ONBOARDING_PLATFORMS: Record<string, OnboardingPlatform> = {
  [SHOPIFY_PLATFORM_ID]: {
    storeField: {
      label: 'Shopify shop domain',
      placeholder: 'store.myshopify.com',
      helperText: 'Use the store hostname, not a storefront URL.',
      testId: 'ella-onboarding-shopify-domain',
    },
    getStoreError: getShopifyStoreError,
    isStoreLocked: () => false,
    formatStoreAddress: (shopDomain) => shopDomain.trim() || undefined,
    publishSuccess: SKIPIFY_ASSISTED_PUBLISH_SUCCESS,
  },
  [BIGCOMMERCE_PLATFORM_ID]: {
    storeField: {
      label: 'BigCommerce store hash',
      placeholder: 'abc123storehash',
      helperText: 'Enter the store hash from your BigCommerce admin URL.',
      testId: 'ella-onboarding-bigcommerce-store-hash',
    },
    getStoreError: getBigCommerceStoreError,
    isStoreLocked: () => false,
    formatStoreAddress: (shopDomain) => normalizeBigCommerceExternalId(shopDomain) ?? undefined,
    publishSuccess: SKIPIFY_ASSISTED_PUBLISH_SUCCESS,
  },
  [SQUARESPACE_PLATFORM_ID]: {
    storeField: {
      label: 'Squarespace site',
      placeholder: 'wombat-gopher-t4x8.squarespace.com',
      helperText:
        "Paste the built-in *.squarespace.com address, or just the subdomain. You'll connect it from Provisioning after you save your agent.",
      lockedHelperText: 'This site is already connected. Manage it from Provisioning.',
      testId: 'ella-onboarding-squarespace-site',
    },
    getStoreError: (shopDomain, { squarespaceLink }) =>
      getSquarespaceSiteError(shopDomain, squarespaceLink),
    isStoreLocked: ({ squarespaceLink }) => Boolean(squarespaceLink?.isLocked),
    formatStoreAddress: (shopDomain) => {
      const subdomain = normalizeSquarespaceExternalId(shopDomain);
      return subdomain ? `${subdomain}.squarespace.com` : undefined;
    },
    publishSuccess: {
      body: 'Connect your Squarespace site and add the shopping agent to it from Provisioning. Once that is done and your catalog is synced, your agent can go live on your storefront.',
      nextSteps: [
        'Connect your Squarespace site',
        'Add the shopping agent to your site',
        'Complete category catalog sync',
        'Enable your agent on your storefront',
      ],
      continueLabel: 'Go to Provisioning',
      continuePath: agentHubPaths.PROVISIONING_SQUARESPACE,
    },
  },
};

const UNKNOWN_PLATFORM: OnboardingPlatform = {
  ...noStoreField,
  publishSuccess: SKIPIFY_ASSISTED_PUBLISH_SUCCESS,
};

/** Platforms without an entry get no store field. */
export const getOnboardingPlatform = (platformId: string): OnboardingPlatform =>
  ONBOARDING_PLATFORMS[platformId] ?? UNKNOWN_PLATFORM;
